import { createElement } from 'react';
import { v4 as uuid } from 'uuid';
import { Editor, Transforms } from 'slate';
import {
  P,
  TABLE,
  TBODY,
  TD,
  TFOOT,
  TH,
  THEAD,
  TR,
} from '@plone/volto-slate/constants';
import { syncCreateTableBlock } from '@plone/volto-slate/blocks/Table/deconstruct';
import { blockTagDeserializer } from '@plone/volto-slate/editor/deserialize';

import { expandPastedSpans, type TableRow } from './mergeCells';

const MAX_SPAN = 1000;

const parseSpan = (value: string | null) => {
  const span = Number.parseInt(value || '', 10);
  return Number.isNaN(span) || span < 1 ? 1 : Math.min(span, MAX_SPAN);
};

const cellDeserializer =
  (tag: string) => (editor: any, el: HTMLElement, options: any) => {
    const node: any = blockTagDeserializer(tag)(editor, el, options);
    const colSpan = parseSpan(el.getAttribute('colspan'));
    const rowSpan = parseSpan(el.getAttribute('rowspan'));
    if (colSpan > 1) node.colSpan = colSpan;
    if (rowSpan > 1) node.rowSpan = rowSpan;
    return node;
  };

export const withCellSpans = (editor: any) => {
  editor.htmlTagsToSlate = {
    ...editor.htmlTagsToSlate,
    TH: cellDeserializer(TH),
    TD: cellDeserializer(TD),
  };
  return editor;
};

export const withStandaloneTableBlock = (editor: any) => {
  const { insertFragment } = editor;
  editor.insertFragment = (...args: unknown[]) => {
    insertFragment(...args);
    const blockProps = editor.getBlockProps?.();
    if (
      !blockProps ||
      blockProps.detached ||
      blockProps.data?.disableNewBlocks
    ) {
      return;
    }
    if (editor.children.length === 1 && editor.children[0].type === TABLE) {
      Transforms.insertNodes(
        editor,
        { type: P, children: [{ text: '' }] } as any,
        { at: [1] },
      );
    }
  };
  return editor;
};

const cellElement =
  (Tag: 'td' | 'th') =>
  ({ attributes, children, element }: any) =>
    createElement(
      Tag,
      {
        ...attributes,
        colSpan: element?.colSpan,
        rowSpan: element?.rowSpan,
      },
      children,
    );

export const TableCellElement = cellElement('td');
export const TableHeaderCellElement = cellElement('th');

const collectRows = (fragment: any): TableRow[] =>
  fragment.children
    .filter((row: any) => row.type === TR)
    .map((row: any) => ({
      key: uuid(),
      cells: row.children
        .filter((cell: any) => cell.type === TD || cell.type === TH)
        .map((cell: any) => ({
          key: uuid(),
          type: cell.type === TH ? 'header' : 'data',
          value: JSON.parse(JSON.stringify(cell.children)),
          ...(cell.colSpan > 1 && { colSpan: cell.colSpan }),
          ...(cell.rowSpan > 1 && { rowSpan: cell.rowSpan }),
        })),
    }));

const collectTableRows = (table: any): TableRow[] => {
  const rowsOf = (type: string) =>
    table.children
      .filter((fragment: any) => fragment.type === type)
      .slice(-1)
      .flatMap(collectRows);
  return expandPastedSpans([
    ...rowsOf(THEAD),
    ...rowsOf(TBODY),
    ...rowsOf(TFOOT),
  ]);
};

export const extractTablesWithSpans = (editor: any, pathRef: any) => {
  const tableNodes = Array.from(
    Editor.nodes(editor, {
      at: pathRef.current,
      match: (node: any) => node.type === TABLE,
    }),
  );
  const tables = tableNodes.map(([node]) => collectTableRows(node));

  Transforms.removeNodes(editor, {
    at: pathRef.current,
    match: (node: any) => node.type === TABLE,
  });

  return tables.map((rows) => syncCreateTableBlock(rows));
};
